import classNames from 'classnames';
import React, { FC, useEffect, useRef, useState } from 'react';
import { circleApi } from 'src/api/circles';
import { CircleConstants } from 'src/api/constants/circle.constants';
import { usersApi } from 'src/api/user';
import { UploadIcon } from 'src/assets/icons/upload-icon';
import { MaterialCircularProgress } from 'src/components/material/CircularProgress';
import { VideoCropModal } from 'src/components/common/VideoCropModal/VideoCropModal';
import { getVideoMetadata } from 'src/utils/videoUtils';
import styles from './Video.module.scss';

interface UploadVideoProps {
  defaultVideo: string;
  onUpdate: (fileId: string | File) => void;
  classNameVideoContainer?: string;
  children?: React.ReactNode;
  helperText?: string;
}

export const UploadVideo: FC<UploadVideoProps> = ({
  defaultVideo,
  onUpdate,
  classNameVideoContainer,
  children,
  helperText,
}) => {
  const [previewVideo, setPreviewVideo] = useState<string>('');
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [cropFile, setCropFile] = useState<File | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (defaultVideo) {
      setPreviewVideo(defaultVideo);
    }
  }, [defaultVideo]);

  const handleVideoClick = () => {
    fileRef.current?.click();
  };

  const processFile = async (file: File) => {
    const isValid = await validateVideoDuration(file);
    if (!isValid) {
      return;
    }

    const { isVertical } = await getVideoMetadata(file);
    if (!isVertical) {
      setCropFile(file);
      return;
    }

    await handleFileUpload(file);
  };

  const handleCropConfirm = async (croppedFile: File) => {
    setCropFile(null);
    await handleFileUpload(croppedFile);
  };

  const handleInputChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    setError(null);
    const uploadedFile = e.target.files?.[0];
    if (uploadedFile) {
      await processFile(uploadedFile);
    }
  };

  const validateVideoDuration = (file: File): Promise<boolean> => {
    return new Promise((resolve) => {
      const video = document.createElement('video');
      video.preload = 'metadata';
      video.src = URL.createObjectURL(file);
      video.onloadedmetadata = () => {
        URL.revokeObjectURL(video.src);
        if (video.duration > CircleConstants.maxChallengeLengthInSeconds) {
          setError(
            `Video duration should not exceed ${CircleConstants.maxChallengeLengthInSeconds} seconds.`
          );
          resolve(false);
        } else {
          resolve(true);
        }
      };
    });
  };

  const handleFileUpload = async (file: File) => {
    setIsUploading(true);
    try {
      const response = await usersApi.uploadFile({
        contentType: file.type,
        name: file.name,
        type: 'video-post',
      });
      const { uploadURL, key } = response;

      const uploadResponse = await fetch(uploadURL, {
        method: 'PUT',
        headers: { 'Content-Type': file.type },
        body: file,
      });
      if (!uploadResponse.ok) {
        throw new Error(`Upload failed with status: ${uploadResponse.status}`);
      }

      const previewUrl = URL.createObjectURL(file);
      setPreviewVideo(previewUrl);

      const videoId = await circleApi.getVideoId(key);
      onUpdate(videoId);
    } catch (error) {
      console.error('Error uploading file:', error);
      setError('Error uploading file. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    setError(null);
    const file = e.dataTransfer.files[0];
    if (file) {
      await processFile(file);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.text}>
        <div className={styles.title}>Video</div>
        {/* <div>
          <a
            href="/how-it-works#info-video"
            target="_blank"
            rel="noopener noreferrer"
            className={styles.infoIcon}
          >
            <InfoOutlinedIcon style={{ color: 'red', cursor: 'pointer' }} />
          </a>
        </div> */}
      </div>
      <div className={styles.description}>
        This is your call to action for Members to attempt a daily activity in
        pursuit of the Circle's purpose.
      </div>
      <div
        className={classNames(styles.videoContainer, classNameVideoContainer, {
          [styles.dragging]: isDragging,
          [styles.isVideoUploaded]: previewVideo && !isUploading,
        })}
        onClick={handleVideoClick}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {!previewVideo && !isUploading && (
          <div className={styles.videoWrapper}>
            <div className={styles.icon}>
              <UploadIcon />
            </div>
            <div className={styles.uploadText}>Select video to upload</div>
            <div className={styles.dragText}>Or drag and drop</div>
          </div>
        )}
        {isUploading && (
          <MaterialCircularProgress
            size={30}
            height="inherit"
            message="Uploading..."
          />
        )}
        {previewVideo && !isUploading && (
          <video
            src={previewVideo}
            controls
            className={styles.previewVideo}
            style={{ width: '100%', height: '150px', display: 'block' }}
          >
            Your browser does not support the video tag.
          </video>
        )}
      </div>
      {error && <div className={styles.errorText}>{error}</div>}
      <div className={styles.maxDurationText}>
        Maximum {CircleConstants.maxChallengeLengthInSeconds} seconds
      </div>
      <input
        accept=".mp4, .mov, .avi"
        type="file"
        className={styles.hiddenInput}
        ref={fileRef}
        onChange={handleInputChange}
      />

      {cropFile && (
        <VideoCropModal
          open={true}
          videoFile={cropFile}
          onConfirm={handleCropConfirm}
          onCancel={() => {
            setCropFile(null);
            if (fileRef.current) {
              fileRef.current.value = '';
            }
          }}
        />
      )}
    </div>
  );
};
